// components/HistorySupplierSelector.tsx

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSupplier } from '../../../../hooks/useSupplier';
import { usePayableTable } from '../../table/hooks/usePayableTable';

import SectionInfoHeader from '../../../../components/common/SectionInfoHeader';
import SvgIcon from '../../../../components/SvgIcon/SvgIcon';
import QuickAddSupplier from '../../../../components/QuickAdd/Supplier/QuickAddSupplier';
import SimpleSearchSelect from '../../../../components/Forms/Input/SimpleSearchSelect';

import SearchSelectionCard from '../../payments/components/SearchSelectionCard/SearchSelectionCard';

const HistorySupplierSelector = () => {
  const navigate = useNavigate();

  const { handlerGetSuppliersForDropdown, suppliersDropdown, saveSupplier } =
    useSupplier();

  const { fetchPaymentHistoryPayablesBySupplier, pendingSupplierPayables } =
    usePayableTable();

  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(
    null
  );

  const [selectedPayableId, setSelectedPayableId] = useState<number | null>(
    null
  );

  /**
   * 🔥 cargar proveedores
   */
  useEffect(() => {
    handlerGetSuppliersForDropdown();
  }, []);

  /**
   * 🔥 cargar cuentas del proveedor
   */
  useEffect(() => {
    if (!selectedSupplierId) return;

    fetchPaymentHistoryPayablesBySupplier(selectedSupplierId);
  }, [selectedSupplierId]);

  /**
   * 🔥 navegación al historial
   */
  const handlePayableSelect = (item: {
    id: number;
    number: string;
    issueDate: string;
    dueDate: string;
    totalAmount: number;
    paidAmount: number;
    balance: number;
    status: string;
    daysOverdue: number;
  }) => {
    setSelectedPayableId(item.id);

    navigate(`/finance/cxp/payment-history/${item.id}`);
  };

  /**
   * 🔥 búsqueda backend
   */
  const handleSearchPayables = async (search: string) => {
    if (!selectedSupplierId) return;

    await fetchPaymentHistoryPayablesBySupplier(selectedSupplierId, search);
  };

  return (
    <div className="px-6 py-6">
      <SectionInfoHeader
        icon={<SvgIcon name="user" />}
        title="Historial de Pagos a Proveedores"
        description="Busca un proveedor para consultar el historial de pagos."
        color="bg-danger"
      />

      {/* Quick Add */}
      <div className="mb-4">
        <QuickAddSupplier
          onReload={handlerGetSuppliersForDropdown}
          onSupplierCreated={saveSupplier}
        />
      </div>

      {/* Proveedor */}
      <div className="mb-5 max-w-xl">
        <SimpleSearchSelect
          label="Proveedor"
          name="supplier"
          iconKey="user"
          selectedValue={selectedSupplierId}
          onSelect={(option) => {
            setSelectedSupplierId(option.id);
            setSelectedPayableId(null);
          }}
          options={suppliersDropdown}
          required
        />
      </div>

      {/* Cuentas */}
      <SearchSelectionCard
        title="Seleccionar cuenta"
        description="Busca una cuenta para consultar su historial de pagos"
        label="Buscar cuenta"
        name="payable-history"
        iconKey="invoice"
        options={pendingSupplierPayables}
        selectedValue={selectedPayableId}
        onSelect={handlePayableSelect}
        onSearch={handleSearchPayables}
        listTitle="Cuentas disponibles"
        emptyMessage="No hay cuentas disponibles para este proveedor"
        disabled={!selectedSupplierId}
        required
      />
    </div>
  );
};

export default HistorySupplierSelector;
